import React, { useEffect, useState } from "react";

/**
 * Dark premium lifestyle image card with hover zoom and load fallback.
 * Mood imagery only — not product photos or fake customer faces.
 *
 * @param {{
 *   src: string,
 *   alt: string,
 *   placeholderLabel?: string,
 *   title?: string,
 *   copy?: string,
 *   className?: string,
 *   aspectClass?: string,
 *   lazy?: boolean,
 *   width?: number,
 *   height?: number,
 *   tone?: "default" | "bright",
 *   children?: React.ReactNode,
 * }} props
 */
export default function LifestyleImageCard({
  src,
  alt,
  placeholderLabel = "Lifestyle photo coming soon",
  title,
  copy,
  className = "",
  aspectClass = "aspect-[4/3]",
  lazy = true,
  width,
  height,
  tone = "default",
  children,
}) {
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
  }, [src]);

  return (
    <article
      className={`image-card ${tone === "bright" ? "image-card--bright" : ""} ${aspectClass} ${className}`}
    >
      {!missing && src ? (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={lazy ? "lazy" : "eager"}
          decoding="async"
          onError={() => setMissing(true)}
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 bg-moss">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-gold/80 mb-3" aria-hidden />
          <p className="text-sm font-medium text-ivory m-0">{placeholderLabel}</p>
        </div>
      )}
      {(title || copy || children) && (
        <div className="image-card-content">
          {title ? (
            <h3 className="font-sans font-semibold text-xl sm:text-[1.35rem] md:text-2xl text-ivory m-0 leading-tight">
              {title}
            </h3>
          ) : null}
          {copy ? (
            <p className="mt-2 text-[15px] sm:text-base text-ivory/90 leading-snug m-0 max-w-[18rem]">{copy}</p>
          ) : null}
          {children}
        </div>
      )}
    </article>
  );
}
